import { NativeVideoDiagnosticsCollector } from './NativeVideoDiagnostics';
import { prepareReceiverOffer } from './ReceiveLevels';
import { NativeVideoMessage, parseNativeVideoMessage } from './NativeVideoProtocol';

export interface NativeVideoClientOptions {
  send(message: NativeVideoMessage): boolean;
  createPeer(): RTCPeerConnection;
  onVideo(track: MediaStreamTrack | null): void;
  capabilities(): RTCRtpCapabilities | null;
}

/** Owns only native video. Audio/input remain on the authenticated primary peer. */
export class NativeVideoClient {
  private pc: RTCPeerConnection | null = null;
  private session = '';
  private diagnosticsEnabled = false;
  private candidates: RTCIceCandidateInit[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private chain = Promise.resolve();
  private disposed = false;
  constructor(private readonly options: NativeVideoClientOptions) {}

  /** Called when the reliable signaling channel opens. Old hosts ignore this. */
  hello(): void {
    this.options.send({ nativeVideo: 1, type: 'hello' });
  }

  /** Serialize answers and candidates; late signals cannot revive a closed peer. */
  receive(data: unknown): void {
    const message = parseNativeVideoMessage(data);
    if (!message) return;
    this.chain = this.chain
      .then(() => (this.disposed ? undefined : this.apply(message)))
      .catch((error) => this.fallback(`Native video negotiation failed (${error instanceof Error ? error.name : 'unknown error'})`));
  }

  private async apply(m: NativeVideoMessage): Promise<void> {
    if (m.type === 'configure') {
      await this.start(m);
      return;
    }
    const pc = this.pc;
    if (!pc || m.session !== this.session) return;
    if (m.type === 'answer') {
      this.diagnosticsEnabled = m.diagnosticsEnabled === true;
      await pc.setRemoteDescription({ type: 'answer', sdp: m.sdp });
      if (pc !== this.pc) return;
      for (const candidate of this.candidates.splice(0)) await pc.addIceCandidate(candidate);
    } else if (m.type === 'ice' && m.candidate) {
      if (pc.remoteDescription) await pc.addIceCandidate(m.candidate);
      else if (this.candidates.length < 256) this.candidates.push(m.candidate);
    } else if (m.type === 'fallback' || m.type === 'close') this.close();
  }

  private async start(m: NativeVideoMessage): Promise<void> {
    this.close();
    this.session = m.session!;
    const mime = `video/${m.codec === 'hevc' ? 'h265' : m.codec}`;
    const codecs = this.options.capabilities()?.codecs.filter((c) => c.mimeType.toLowerCase() === mime) ?? [];
    if (!codecs.length) {
      this.fallback('Requested native codec is unavailable');
      return;
    }
    const pc = this.options.createPeer();
    this.pc = pc;
    const session = this.session;
    const send = (message: Partial<NativeVideoMessage> & Pick<NativeVideoMessage, 'type'>) =>
      this.options.send({ ...message, nativeVideo: 1, session });
    const diagnostics = new NativeVideoDiagnosticsCollector();
    let iceErrorCode: number | undefined;
    pc.onicecandidateerror = (event) => { if (this.pc === pc) iceErrorCode = event.errorCode; };
    let offerSent = false;
    const early: RTCIceCandidateInit[] = [];
    pc.onicecandidate = (event) => {
      if (this.pc !== pc || !event.candidate) return;
      const candidate = event.candidate.toJSON();
      if (offerSent) send({ type: 'ice', candidate });
      else early.push(candidate);
    };
    let track: MediaStreamTrack | null = null,
      ready = false,
      busy = false;
    let lastFrames = 0,
      lastBytes = 0,
      lastProgress = Date.now(),
      lastArrival = 0;
    const started = Date.now();
    let disconnectedAt = 0;
    pc.ontrack = (event) => {
      if (event.track.kind === 'video') track = event.track;
    };
    pc.onconnectionstatechange = () => {
      disconnectedAt = pc.connectionState === 'disconnected' ? Date.now() : 0;
      if (this.pc === pc && ['failed', 'closed'].includes(pc.connectionState)) this.fallback('Native video disconnected');
    };
    this.timer = setInterval(async () => {
      if (busy || this.pc !== pc) return;
      busy = true;
      try {
        const report = await pc.getStats();
        if (this.pc !== pc) return;
        if (this.diagnosticsEnabled) {
          // Isolate collection as well as delivery from the media watchdog.
          try {
            const snapshot = diagnostics.sample(report, pc.connectionState, Date.now(), iceErrorCode, performance.now());
            if (snapshot) send({ type: 'diagnostics', diagnostics: snapshot });
          } catch {}
        }
        let video: any;
        report.forEach((s) => {
          if (s.type === 'inbound-rtp' && s.kind === 'video') video = s;
        });
        const now = Date.now(),
          frames = video?.framesDecoded ?? 0,
          bytes = video?.bytesReceived ?? 0;
        if (disconnectedAt && now - disconnectedAt > 5000) {
          this.fallback('Native video connection was lost');
          return;
        }
        if (frames > lastFrames) lastProgress = now;
        if (bytes > lastBytes) {
          if (!lastArrival || now - lastArrival > 3000) lastProgress = now;
          lastArrival = now;
        }
        if (!ready && track && frames > 0) {
          ready = true;
          this.options.onVideo(track);
          let mime: string | undefined;
          report.forEach((entry) => {
            if (entry.id === video.codecId) mime = entry.mimeType?.toLowerCase();
          });
          const codec = mime === 'video/h265' ? 'hevc' : mime === 'video/av1' ? 'av1' : mime === 'video/h264' ? 'h264' : undefined;
          send({ type: 'ready', codec });
        }
        if ((!ready && now - started > 20000) || (bytes > lastBytes && now - lastProgress > 8000))
          this.fallback('Native video did not make decoding progress');
        lastFrames = frames;
        lastBytes = bytes;
      } catch {
        if (this.pc === pc) this.fallback('Native video stats failed');
      } finally {
        busy = false;
      }
    }, 500);
    const transceiver = pc.addTransceiver('video', { direction: 'recvonly' });
    transceiver.setCodecPreferences(codecs);
    const offer = await pc.createOffer();
    const checked = await prepareReceiverOffer(offer, { codec: m.codec! });
    if (this.pc !== pc) return;
    await pc.setLocalDescription(checked.offer);
    if (this.pc !== pc) return;
    if (!send({ type: 'offer', sdp: pc.localDescription!.sdp })) throw new Error('Signaling channel closed');
    offerSent = true;
    for (const candidate of early) send({ type: 'ice', candidate });
  }

  private fallback(reason: string): void {
    const session = this.session;
    this.close();
    if (session) this.options.send({ nativeVideo: 1, type: 'fallback', session, reason });
  }

  /** Release native media when the parent player connection ends. */
  close(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    const pc = this.pc;
    this.pc = null;
    this.candidates = [];
    this.session = '';
    this.diagnosticsEnabled = false;
    if (pc) {
      pc.onicecandidateerror = null;
      pc.ontrack = pc.onicecandidate = pc.onconnectionstatechange = null;
      pc.close();
    }
    this.options.onVideo(null);
  }
  /** Permanently cancel queued signaling when the parent player closes. */
  destroy(): void {
    this.disposed = true;
    this.close();
  }
  /** Expose receiver stats to the existing player diagnostics. */
  getPeerConnection(): RTCPeerConnection | null {
    return this.pc;
  }
}
